import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Tooltip, DialogTitle, Grid, IconButton, Switch, Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {DeleteIcon} from '@oc/icons'
import {OcGenericDialogStatus, OcGenericDialogTitleProps} from './OcGenericDialogInterfacesExport'
import {OcGenericDialogState} from './OcGenericEditState'
import {xlBreakpoint} from './OcGenericDialogInterfaces'
import {messages} from '../translations'

interface OcGenericDialogTitleComponentProps<SourceType> extends OcGenericDialogTitleProps {
    dialogState: OcGenericDialogState<SourceType>
}

const useStyles = makeOcStyles(theme => {
    const borderProps = `1px solid ${theme.palette.divider}`
    return {
        titleRoot: {
            padding: 0
        },
        titleRow: {
            alignItems: 'center',
            borderBottom: borderProps
        },
        rowPaddingFull: {
            padding: '12px 24px'
        },
        rowPaddingSides: {
            padding: '0 24px'
        },
        middleGrid: {
            justifyContent: 'center',
            [theme.breakpoints.down(xlBreakpoint)]: {
                justifyContent: 'left'
            }
        },
        rightGrid: {
            justifyContent: 'right',
            [theme.breakpoints.down(xlBreakpoint)]: {
                justifyContent: 'left'
            }
        }
    }
})

function _OcGenericDialogStatusComponent<SourceType>(props: OcGenericDialogTitleComponentProps<SourceType>) {
    const {secondRow, dialogState} = props
    const statusProps = secondRow?.status
    const deletable = secondRow?.status?.deletable
    const activable = secondRow?.status?.activable
    const status = dialogState.status

    const deleteCallback = React.useCallback(() => {
        dialogState.setStatus(OcGenericDialogStatus.Deleted)
    }, [dialogState])

    const changeActiveCallback = React.useCallback(
        (checked: boolean) => {
            if (checked) {
                dialogState.setStatus(OcGenericDialogStatus.Active)
            } else {
                dialogState.setStatus(OcGenericDialogStatus.Deactivated)
            }
        },
        [dialogState]
    )

    if (!statusProps || dialogState.createMode || typeof dialogState.status === 'undefined') {
        return null
    }

    return (
        <div style={{display: 'flex', alignItems: 'center'}}>
            <Typography>{statusProps.label}</Typography>
            {deletable ? (
                <Tooltip
                    title={
                        status === OcGenericDialogStatus.Deleted
                            ? messages.getText('genericDialog', 'alreadyDeletedTooltip')
                            : messages.getText('genericDialog', 'deleteTooltip')
                    }
                >
                    <span>
                        <IconButton
                            onClick={deleteCallback}
                            disabled={statusProps.disableDelete || status === OcGenericDialogStatus.Deleted}
                            size="large"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : undefined}
            {activable ? (
                <Tooltip
                    title={
                        status === OcGenericDialogStatus.Active
                            ? messages.getText('genericDialog', 'deactivateTooltip')
                            : messages.getText('genericDialog', 'setToActiveTooltip')
                    }
                >
                    <span>
                        <Switch
                            color="primary"
                            checked={status === OcGenericDialogStatus.Active}
                            disabled={statusProps.disableSwitch}
                            onChange={(_, checked) => changeActiveCallback(checked)}
                        />
                    </span>
                </Tooltip>
            ) : undefined}
        </div>
    )
}

export const OcGenericDialogStatusComponent = observer(_OcGenericDialogStatusComponent)

function _OcGenericDialogTitleComponent<SourceType>(props: OcGenericDialogTitleComponentProps<SourceType>) {
    const {firstRow, secondRow, dialogState} = props
    const {classes, cx} = useStyles()

    return (
        <DialogTitle className={classes.titleRoot}>
            <Grid container>
                {firstRow ? (
                    <Grid container item xs={12} className={cx(classes.titleRow, classes.rowPaddingFull)}>
                        <Grid
                            container
                            item
                            xl={4}
                            lg={6}
                            md={12}
                            sm={12}
                            data-cy="oc-generic-dialog-title-top-left-grid"
                        >
                            {firstRow.left ? firstRow.left : undefined}
                        </Grid>
                        <Grid
                            container
                            item
                            xl={4}
                            lg={6}
                            md={12}
                            sm={12}
                            className={classes.middleGrid}
                            data-cy="oc-generic-dialog-title-top-middle-grid"
                        >
                            {firstRow.middle ? firstRow.middle : undefined}
                        </Grid>
                        <Grid
                            container
                            item
                            xl={4}
                            lg={6}
                            md={12}
                            sm={12}
                            className={classes.rightGrid}
                            data-cy="oc-generic-dialog-title-top-right-grid"
                        >
                            {firstRow.right ? firstRow.right : undefined}
                        </Grid>
                    </Grid>
                ) : undefined}
                {secondRow ? (
                    <Grid
                        container
                        item
                        xs={12}
                        className={cx({
                            [classes.titleRow]: true,
                            [classes.rowPaddingSides]:
                                typeof secondRow.status !== 'undefined' && typeof dialogState.status !== 'undefined',
                            [classes.rowPaddingFull]:
                                typeof secondRow.status === 'undefined' || typeof dialogState.status === 'undefined'
                        })}
                    >
                        <Grid
                            container
                            item
                            xl={10}
                            lg={8}
                            md={6}
                            sm={12}
                            data-cy="oc-generic-dialog-title-bottom-status-grid"
                        >
                            {secondRow.left ? secondRow.left : undefined}
                        </Grid>
                        <Grid container item xl={2} lg={4} md={6} sm={12} justifyContent="flex-end">
                            <OcGenericDialogStatusComponent {...props} />
                        </Grid>
                    </Grid>
                ) : undefined}
            </Grid>
        </DialogTitle>
    )
}

export const OcGenericDialogTitleComponent = observer(_OcGenericDialogTitleComponent)
